import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronDown, ChevronUp, UserPlus } from "lucide-react"
import { useEffect, useState } from "react"

import type {
  GuildDirectoryMember,
  ManualParticipantMode,
  MartialArtPath,
  WeeklyAvailability,
} from "@/lib/guild-war-api"

import { Button } from "@/components/button"
import { CandidatePoolManualAddCard } from "@/components/guild-war/candidate-pool-manual-add-card"
import { guildWarApi } from "@/lib/guild-war-api"
import { memberDisplayLabel } from "@/lib/member-display-label"
import { guildWarKeys } from "@/lib/query-keys"

export function CandidatePoolManualAddSection({
  effectiveWeekId,
}: {
  effectiveWeekId: string
}) {
  const queryClient = useQueryClient()

  const [memberSearch, setMemberSearch] = useState("")
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState("")
  const [isMemberSearchOpen, setIsMemberSearchOpen] = useState(false)
  const [selectedMember, setSelectedMember] =
    useState<GuildDirectoryMember | null>(null)
  const [manualMode, setManualMode] = useState<ManualParticipantMode>("weekly")
  const [manualAvailability, setManualAvailability] =
    useState<WeeklyAvailability>("both")
  const [manualPrimaryPath, setManualPrimaryPath] = useState<
    MartialArtPath | ""
  >("")
  const [manualSecondaryPath, setManualSecondaryPath] = useState<
    MartialArtPath | "none"
  >("none")
  const [overrideSavedPaths, setOverrideSavedPaths] = useState(false)
  const [manualActionMessage, setManualActionMessage] = useState<string | null>(
    null,
  )
  const [isManualAddOpen, setIsManualAddOpen] = useState(false)

  useEffect(() => {
    const timeout = globalThis.setTimeout(() => {
      setDebouncedMemberSearch(memberSearch.trim())
    }, 200)

    return () => {
      globalThis.clearTimeout(timeout)
    }
  }, [memberSearch])

  const memberSearchQuery = useQuery({
    queryKey: guildWarKeys.memberSearch(debouncedMemberSearch),
    queryFn: () =>
      guildWarApi.searchGuildMembers({
        query: debouncedMemberSearch,
        limit: 8,
      }),
    enabled:
      debouncedMemberSearch.length >= 2 &&
      selectedMember === null &&
      isMemberSearchOpen,
  })

  const selectedMemberProfileQuery = useQuery({
    queryKey: guildWarKeys.membersByIds(
      selectedMember ? [selectedMember.memberId] : [],
    ),
    queryFn: () =>
      guildWarApi.listGuildWarMemberIdentitiesByIds(
        selectedMember ? [selectedMember.memberId] : [],
      ),
    enabled: selectedMember !== null,
  })

  const selectedMemberProfile = selectedMemberProfileQuery.data?.[0] ?? null
  const hasSavedPrimaryPath = Boolean(selectedMemberProfile?.primaryPathCode)
  const shouldRequirePathInput =
    overrideSavedPaths ||
    (selectedMember !== null &&
      selectedMemberProfileQuery.isSuccess &&
      !hasSavedPrimaryPath)

  const addParticipantMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveWeekId || !selectedMember) {
        throw new Error("Choose a week and member before adding a participant")
      }

      if (shouldRequirePathInput && !manualPrimaryPath) {
        throw new Error("Select a primary path before adding a participant")
      }

      const skillPath: MartialArtPath | null =
        shouldRequirePathInput && manualPrimaryPath ? manualPrimaryPath : null
      const secondarySkillPath = shouldRequirePathInput
        ? manualSecondaryPath === "none"
          ? null
          : manualSecondaryPath
        : null

      if (manualMode === "permanent") {
        return guildWarApi.grantPermanentMembershipForMember({
          memberId: selectedMember.memberId,
          weekId: effectiveWeekId,
          skillPath,
          secondarySkillPath,
          overrideSavedPaths,
          guildDisplayName: selectedMember.displayName,
        })
      }

      return guildWarApi.setWeeklySignupForMember(selectedMember.memberId, {
        weekId: effectiveWeekId,
        availability: manualAvailability,
        skillPath,
        secondarySkillPath,
        overrideSavedPaths,
        guildDisplayName: selectedMember.displayName,
      })
    },
    onSuccess: async () => {
      const memberLabel = selectedMember
        ? memberDisplayLabel({
            displayName: selectedMember.displayName,
            memberId: selectedMember.memberId,
          })
        : "Member"

      const availabilityLabel =
        manualAvailability === "both"
          ? "both days"
          : manualAvailability === "saturday"
            ? "Saturday"
            : manualAvailability === "sunday"
              ? "Sunday"
              : manualAvailability

      setManualActionMessage(
        manualMode === "permanent"
          ? `Granted permanent membership to ${memberLabel} using ${effectiveWeekId} as the reference week.`
          : `Added ${memberLabel} to ${effectiveWeekId} for ${availabilityLabel}.`,
      )
      setSelectedMember(null)
      setMemberSearch("")
      setDebouncedMemberSearch("")
      setIsMemberSearchOpen(false)
      setManualMode("weekly")
      setManualAvailability("both")
      setManualPrimaryPath("")
      setManualSecondaryPath("none")
      setOverrideSavedPaths(false)

      await queryClient.invalidateQueries({
        queryKey: guildWarKeys.all,
      })
    },
    onError: (error) => {
      setManualActionMessage(
        error instanceof Error ? error.message : "Failed to add participant",
      )
    },
  })

  const showMemberResults =
    isMemberSearchOpen &&
    selectedMember === null &&
    debouncedMemberSearch.length >= 2
  const manualAddBlockedReason = !effectiveWeekId
    ? "Choose a week below before adding a participant."
    : !selectedMember
      ? "Select a guild member to enable manual add."
      : shouldRequirePathInput && !manualPrimaryPath
        ? "Choose a primary path to enable manual add."
        : null

  const clearSelectedMember = () => {
    setSelectedMember(null)
    setMemberSearch("")
    setDebouncedMemberSearch("")
    setIsMemberSearchOpen(false)
    setManualActionMessage(null)
    setOverrideSavedPaths(false)
  }

  return (
    <div className="grid gap-2">
      <div className="sticky top-2 z-20 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/55 bg-card/82 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <UserPlus className="size-4" />
          Add participant
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={
              Boolean(manualAddBlockedReason) ||
              addParticipantMutation.isPending
            }
            onClick={() => {
              setManualActionMessage(null)
              void addParticipantMutation.mutateAsync()
            }}
          >
            {addParticipantMutation.isPending ? "Adding..." : "Add participant"}
          </Button>
          <Button
            unstyled
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/70 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
            onClick={() => setIsManualAddOpen((current) => !current)}
          >
            {isManualAddOpen ? (
              <>
                Hide
                <ChevronUp className="size-3.5" />
              </>
            ) : (
              <>
                Show
                <ChevronDown className="size-3.5" />
              </>
            )}
          </Button>
        </div>
      </div>

      {isManualAddOpen ? (
        <CandidatePoolManualAddCard
          selectedMember={selectedMember}
          memberSearch={memberSearch}
          showMemberResults={showMemberResults}
          memberSearchQuery={memberSearchQuery}
          manualAddBlockedReason={manualAddBlockedReason}
          manualMode={manualMode}
          manualAvailability={manualAvailability}
          manualPrimaryPath={manualPrimaryPath}
          manualSecondaryPath={manualSecondaryPath}
          overrideSavedPaths={overrideSavedPaths}
          selectedMemberPrimaryPath={
            selectedMemberProfile?.primaryPathCode ?? null
          }
          selectedMemberSecondaryPath={
            selectedMemberProfile?.secondaryPathCode ?? null
          }
          shouldRequirePathInput={shouldRequirePathInput}
          effectiveWeekId={effectiveWeekId}
          addParticipantPending={addParticipantMutation.isPending}
          manualActionMessage={manualActionMessage}
          addParticipantError={addParticipantMutation.isError}
          onClearSelectedMember={clearSelectedMember}
          onMemberSearchFocus={() => {
            if (memberSearch.trim().length >= 2) {
              setIsMemberSearchOpen(true)
            }
          }}
          onMemberSearchEscape={() => setIsMemberSearchOpen(false)}
          onMemberSearchChange={(value) => {
            setManualActionMessage(null)
            setMemberSearch(value)
            setIsMemberSearchOpen(value.trim().length >= 2)
          }}
          onSelectMember={(member) => {
            setSelectedMember(member)
            setMemberSearch(member.displayName ?? member.memberId)
            setDebouncedMemberSearch("")
            setManualActionMessage(null)
            setIsMemberSearchOpen(false)
          }}
          onManualModeChange={(value) => {
            setManualActionMessage(null)
            setManualMode(value)
          }}
          onManualPrimaryPathChange={(value) => {
            setManualActionMessage(null)
            setManualPrimaryPath(value)
          }}
          onManualSecondaryPathChange={(value) => {
            setManualActionMessage(null)
            setManualSecondaryPath(value)
          }}
          onOverrideSavedPathsChange={(value) => {
            setManualActionMessage(null)
            setOverrideSavedPaths(value)
          }}
          onManualAvailabilityChange={(value) => {
            setManualActionMessage(null)
            setManualAvailability(value)
          }}
          onAddParticipant={() => {
            setManualActionMessage(null)
            void addParticipantMutation.mutateAsync()
          }}
        />
      ) : null}
    </div>
  )
}
